import { Container, Graphics } from 'pixi.js';
import type { PublicPlayerAppearance } from '@push.io/shared';
import type { GraphicsProfile } from '../graphics-quality.js';
import { warnCosmeticFallback } from '../fallback-warning.js';

interface TrailParticle {
  x: number;
  y: number;
  alpha: number;
  size: number;
}

const TRAIL_COLOR_MAP: Record<string, number> = {
  cosm_trail_basic: 0x3b82f6,
  cosm_trail_sparks: 0xf97316,
  cosm_trail_dots: 0x22ff88,
  trail_default: 0x3b82f6,
  trail_fire: 0xf97316,
  trail_ghost: 0x8888cc,
  trail_neon: 0x22ff88,
};

const POOL_SIZE = 32;
const VISUAL_MAX_SPEED = 0.12;

export class TrailLayer {
  readonly container: Container;
  private gfx: Graphics;
  private pool: TrailParticle[] = [];
  private active: TrailParticle[] = [];
  private currentTrailId: string | null = null;
  private frameCount = 0;
  private elapsedMs = 0;
  private speed = 0;
  private lastRootX: number | null = null;
  private lastRootY: number | null = null;

  constructor() {
    this.container = new Container();
    this.gfx = new Graphics();
    this.container.addChild(this.gfx);
    for (let i = 0; i < POOL_SIZE; i++) {
      this.pool.push({ x: 0, y: 0, alpha: 0, size: 0 });
    }
  }

  update(deltaMs: number, screenX: number, screenY: number, appearance: PublicPlayerAppearance | null, profile: GraphicsProfile): void {
    this.frameCount++;
    this.elapsedMs += deltaMs;
    const trailId = appearance?.trailId ?? null;
    if (!trailId) {
      this.clear();
      this.currentTrailId = null;
      return;
    }
    this.currentTrailId = trailId;
    const color = TRAIL_COLOR_MAP[trailId] ?? 0x3b82f6;
    if (!(trailId in TRAIL_COLOR_MAP)) warnCosmeticFallback('trail', trailId);

    const maxLen = profile.trailLength;
    while (this.active.length > maxLen) {
      this.recycle(this.active.shift()!);
    }

    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.alpha -= deltaMs / 420;
      if (p.alpha <= 0) {
        this.recycle(this.active.splice(i, 1)[0]);
      }
    }

    if (this.lastRootX !== null && this.lastRootY !== null) {
      const dx = screenX - this.lastRootX;
      const dy = screenY - this.lastRootY;
      for (const p of this.active) {
        p.x -= dx;
        p.y -= dy;
      }
    }
    this.lastRootX = screenX;
    this.lastRootY = screenY;

    const speedRatio = Math.max(0, Math.min(1, this.speed / VISUAL_MAX_SPEED));
    const intervalMs = 16 * profile.trailInterval / Math.max(0.2, speedRatio);
    if (speedRatio > 0.08 && this.elapsedMs >= intervalMs) {
      this.elapsedMs %= intervalMs;
      const p = this.acquire();
      if (p) {
        p.x = 0;
        p.y = 0;
        p.alpha = 0.25 + speedRatio * 0.35;
        p.size = 1.5 + speedRatio * 2;
        this.active.push(p);
      }
    }

    while (this.active.length > maxLen) {
      this.recycle(this.active.shift()!);
    }

    const g = this.gfx;
    g.clear();

    if (trailId === 'cosm_trail_dots' || trailId === 'trail_dots') {
      for (const p of this.active) {
        g.circle(p.x, p.y, Math.max(1.5, p.size * 0.6));
        g.fill({ color, alpha: p.alpha * 0.6 });
      }
    } else if (trailId === 'cosm_trail_sparks' || trailId === 'trail_sparks') {
      for (const p of this.active) {
        g.circle(p.x, p.y, Math.max(1, p.size * 0.4));
        g.fill({ color: 0xffcc00, alpha: p.alpha * 0.7 });
        g.circle(p.x, p.y, Math.max(1.5, p.size * 0.6));
        g.fill({ color, alpha: p.alpha * 0.5 });
      }
    } else {
      for (const p of this.active) {
        g.circle(p.x, p.y, Math.max(1.5, p.size));
        g.fill({ color, alpha: p.alpha * 0.4 });
      }
    }
  }

  setSpeed(vx: number, vy: number): void {
    this.speed = Math.hypot(vx, vy);
  }

  private acquire(): TrailParticle | null {
    return this.pool.pop() ?? null;
  }

  private recycle(p: TrailParticle): void {
    if (this.pool.length < POOL_SIZE) {
      p.alpha = 0;
      p.size = 0;
      this.pool.push(p);
    }
  }

  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  clear(): void {
    while (this.active.length) this.recycle(this.active.pop()!);
    this.gfx.clear();
    this.lastRootX = null;
    this.lastRootY = null;
    this.elapsedMs = 0;
  }

  get activeCount(): number {
    return this.active.length;
  }

  destroy(): void {
    this.gfx.clear();
    this.gfx.destroy();
    this.pool = [];
    this.active = [];
    this.container.removeChildren();
  }
}
